import type { FeatureGridBlockPayload, FeatureGridGroup } from '@opinix-cloud/shared';

interface CapabilitiesProps {
  content: FeatureGridBlockPayload;
}

const iconCx = 'h-5 w-5';

/**
 * Five-section "everything inside the platform" deep-dive. Each
 * group renders as a left column with eyebrow / heading / blurb /
 * icon, and a right column with a 2-up grid of items (title +
 * body). Icons are keyed by `iconKey` and stay in code.
 */
export function Capabilities({ content }: CapabilitiesProps): JSX.Element {
  const groups = content.groups ?? [];
  return (
    <section id="capabilities" className="bg-white py-24 sm:py-32">
      <div className="container-x">
        <div className="max-w-3xl">
          {content.eyebrow && <p className="eyebrow">{content.eyebrow}</p>}
          {content.heading && (
            <h2 className="mt-3 section-title">{content.heading}</h2>
          )}
          {content.blurb && (
            <p className="mt-5 text-lg text-steel-600 leading-relaxed">
              {content.blurb}
            </p>
          )}
        </div>

        <div className="mt-16 space-y-16 sm:space-y-20">
          {groups.map((g) => (
            <CapabilityGroup
              key={g.eyebrow ?? g.heading ?? Math.random().toString(36)}
              group={g}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function CapabilityGroup({ group }: { group: FeatureGridGroup }): JSX.Element {
  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
        <div className="lg:col-span-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-navy-900 text-cyan-400">
            <CapabilityIcon iconKey={group.iconKey} />
          </div>
          {group.eyebrow && <p className="eyebrow mt-5">{group.eyebrow}</p>}
          {group.heading && (
            <h3 className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-navy-900 leading-tight">
              {group.heading}
            </h3>
          )}
          {group.blurb && (
            <p className="mt-4 text-base text-steel-600 leading-relaxed">
              {group.blurb}
            </p>
          )}
        </div>
        <div className="lg:col-span-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7">
            {group.items.map((item) => (
              <div
                key={item.title}
                className="border-l-2 border-cyan-500/40 pl-4"
              >
                <h4 className="text-sm font-bold text-navy-900 tracking-tight">
                  {item.title}
                </h4>
                <p className="mt-1.5 text-sm text-steel-600 leading-relaxed">
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function CapabilityIcon({ iconKey }: { iconKey: string | null | undefined }): JSX.Element {
  switch (iconKey) {
    case 'antenna':
      return (
        <svg viewBox="0 0 24 24" fill="none" className={iconCx} aria-hidden="true">
          <path d="M5 12a7 7 0 0 1 14 0M8 12a4 4 0 0 1 8 0M12 12v9m-3 0h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
    case 'scenario':
      return (
        <svg viewBox="0 0 24 24" fill="none" className={iconCx} aria-hidden="true">
          <path d="M4 4h16v6H4zM4 14h10v6H4zM18 14h2v6h-2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        </svg>
      );
    case 'sync':
      return (
        <svg viewBox="0 0 24 24" fill="none" className={iconCx} aria-hidden="true">
          <path d="M12 2v4M12 18v4M2 12h4M18 12h4M5 5l3 3M16 16l3 3M5 19l3-3M16 8l3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
        </svg>
      );
    case 'clock':
      return (
        <svg viewBox="0 0 24 24" fill="none" className={iconCx} aria-hidden="true">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
          <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'shield-2':
    default:
      return (
        <svg viewBox="0 0 24 24" fill="none" className={iconCx} aria-hidden="true">
          <path d="M3 7h18v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M9 7V5a3 3 0 1 1 6 0v2M8 12h8M8 16h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
  }
}
